import { fetchTokenAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const KEYWORDS =
  "b2b contact company data enrichment intent sales intelligence prospecting discoverorg gtm";

export const zoomInfo = defineProduct({
  name: "ZoomInfo",
  tagLine: "B2B contact and company data platform",
  logo,
  tags: ["productivity", "data"],
  variants: [
    productVariant({
      identifier: "zoomInfo",
      authLabel: "OAuth (current API)",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.zoominfo\\.com/gtm/.*",
      auth: oauthAuth,
      steps: [
        {
          title: "Create an app in the ZoomInfo Developer Portal",
          items: [
            {
              kind: "markdown",
              content:
                "Go to the [ZoomInfo Developer Portal](https://developer.zoominfo.com/) and create an app that uses the **Client Credentials** flow. Assign it the OAuth scopes your workflows need (for example, company and contact data), and pick the integration user whose entitlements the app will run as. Your ZoomInfo admin may need to grant DevPortal access under **Admin → User Management** first.",
            },
          ],
        },
        {
          title: "Enter your OAuth credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Copy the Client ID and Client Secret from your ZoomInfo app.",
            },
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        tokenEndpoint: () => "https://api.zoominfo.com/gtm/oauth/v1/token",
        tokenEndpointAuthMethod: () => "client_secret_basic",
        clientCredentialsFrom: "db",
        scopes: () => [],
      },
      connectionTest: {
        protocol: "http",
        request: () => "https://api.zoominfo.com/gtm/data/v1/users/usage",
      },
    }),
    productVariant({
      identifier: "zoomInfoLegacy",
      authLabel: "Username & password (legacy API)",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.zoominfo\\.com/.*",
      auth: (app) =>
        fetchTokenAuth({
          request: {
            url: "https://api.zoominfo.com/authenticate",
            method: "POST",
            jsonBody: {
              username: app.config.username,
              password: app.secret.password,
            },
          },
          extract: { from: "jsonPath", paths: ["jwt"] },
          apply: { kind: "header", scheme: "Bearer" },
        }),
      steps: [
        {
          title: "Enter your ZoomInfo API credentials",
          items: [
            {
              kind: "markdown",
              content:
                "This uses ZoomInfo’s legacy Enterprise API (`api.zoominfo.com/authenticate`), which ZoomInfo is deprecating. Choose this only if your account still authenticates against the legacy API — otherwise use the **OAuth (current API)** option.\n\nSign in with the username and password for a ZoomInfo user that has API access. API access is a paid add-on, so your ZoomInfo admin may need to enable it under **Admin → User Management** first.",
            },
            {
              kind: "input",
              label: "Username",
              required: true,
              target: { kind: "configuration", field: "username" },
            },
            {
              kind: "input",
              label: "Password",
              required: true,
              target: { kind: "secrets", field: "password" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () =>
          "https://api.zoominfo.com/lookup/inputfields/company/enrich",
      },
    }),
  ],
});
